import { inject } from '@angular/core';
import { catchError, first, map, Observable, of } from 'rxjs';
import { DATABASE_NAME } from 'src/app/config/app.config';
import { LOCAL_DATABASE } from 'src/app/core/enums/storage/local-database.enum';
import { STORE_DATABASE } from 'src/app/core/enums/storage/store-database.enum';
import { Logger } from 'src/app/core/interfaces/Logger';
import { IndexedDBRepository } from 'src/app/shared/interface/indexed-db-storage.model';
import { Account } from '../../domain/model/account.model';
import { IAccountSessionRepository } from '../../domain/repositories/account-session.repository';
import { IAccount } from '../interfaces/account-storage.interface';
import { AccountEntityMapper } from '../mappers/account-entity.mapper';
import { AccountStorageMapper } from '../mappers/account-storage.mapper';

export class AccountSessionAdapter
  extends IndexedDBRepository<IAccount | undefined>
  implements IAccountSessionRepository
{
  private readonly logger = inject(Logger);
  readonly dbName = DATABASE_NAME;
  readonly instanceName = STORE_DATABASE.session;

  get(): Observable<Account | undefined> {
    return this.getItem(LOCAL_DATABASE.account).pipe(
      first(),
      map((account) => (account ? AccountEntityMapper.fromStorage(account) : undefined)),
      catchError((error) => {
        this.logger.error(
          new Error(`[AccountSessionRepository][get] Error getting account`),
          error
        );

        return of(undefined);
      })
    );
  }

  save(account: Account | undefined): Observable<Account | undefined> {
    return this.setItem(
      LOCAL_DATABASE.account,
      account ? AccountStorageMapper.fromEntity(account) : undefined
    ).pipe(
      first(),
      map(() => account),
      catchError((error) => {
        this.logger.error(
          new Error(`[AccountSessionRepository][set] Error setting account`),
          error
        );

        return of(undefined);
      })
    );
  }

  delete(): Observable<void> {
    return this.removeItem(LOCAL_DATABASE.account).pipe(
      first(),
      catchError((error) => {
        this.logger.error(
          new Error(
            `[AccountSessionRepository][delete] Error deleting account`
          ),
          error
        );

        return of(undefined);
      })
    );
  }
}
